import { type Revision, RevisionType } from '../types';
import { mutations } from '../utils';
import {
    addPageCoverInRevision,
    duplicatePageInRevision,
    getPageParentInRevision,
    getUniquePageTitle,
    insertFileInRevision,
    insertPageAfterInRevision,
    insertPageBeforeInRevision,
    insertPageInRevision,
    insertReusableContentInRevision,
    insertTagInRevision,
    insertTagGroupInRevision,
    updateTagGroupInRevision,
    removeTagGroupInRevision,
    setTagGroupInRevision,
    markFileAsEdited,
    markPageAsEdited,
    markRevisionAsEdited,
    movePageAfterInRevision,
    movePageBeforeInRevision,
    movePageCoverInRevision,
    movePageInRevision,
    removeFileInRevision,
    removePageCoverInRevision,
    removePageInRevision,
    removeReusableContentInRevision,
    removeTagInRevision,
    renamePageInRevision,
    resetPageCoverHeightInRevision,
    resolveConflictInRevision,
    restorePageInRevision,
    setPageCoverHeightInRevision,
    setPageDescriptionInRevision,
    setPageIconInRevision,
    updateFileInRevision,
    updatePageInRevision,
    updatePageLayoutInRevision,
    updatePageVisibilityInRevision,
    updateReusableContentInRevision,
    updateRevisionPageVariables,
    updateRevisionVariables,
    updateTagInRevision,
} from './mutations';
import {
    removePageConditionInRevision,
    updatePageConditionInRevision,
} from './mutations/conditionalPages';
import {
    addPageAlternateLink,
    updatePageAlternateLink,
    updatePageCanonicalLink,
} from './mutations/pageMetaLinks';
import type { YRevision } from './revisionToY';

const METHODS = {
    /**
     * Insert a new page.
     * @param page - The page to insert.
     * @param into - The id of the page to insert into. If not defined, the page will be inserted as a root page.
     * @param at - The index to insert the page at. If not defined, the page will be appended at the end.
     */
    insertPage: insertPageInRevision,

    /**
     * Insert a new page after another one.
     * @param page - The page to insert.
     * @param afterPageId - The ID of the page to insert after.
     */
    insertPageAfter: insertPageAfterInRevision,

    /**
     * Insert a new page before another one.
     * @param page - The page to insert.
     * @param boeforePageId - The ID of the page to insert before.
     */
    insertPageBefore: insertPageBeforeInRevision,

    /**
     * Remove a page.
     * @param pageId - The ID of the page to remove.
     */
    removePage: removePageInRevision,

    /**
     * Restore a deleted document page (including subtree roots) using another revision as the structural source of truth.
     * @param page - The page to restore.
     * @param options.sourceRevision - The revision containing the original ordering.
     */
    restorePage: restorePageInRevision,

    /**
     * Update a page in the revision.
     * @param pageId - The ID of the page to update.
     * @param update - The update to apply to the page.
     */
    updatePage: updatePageInRevision,

    /**
     * Update the layout of a page in the revision.
     * @param pageId - The ID of the page to update.
     * @param update - The update to apply to the page.
     */
    updatePageLayout: updatePageLayoutInRevision,

    /**
     * Move a page to a new parent and/or index.
     * @param pageId - The ID of the page to move.
     * @param newParentId - The ID of the new parent page. If not defined, the page will be moved to the root.
     * @param options.at - The new index of the page. If not defined, the page will be appended at the end.
     */
    movePage: movePageInRevision,

    /**
     * Move a page after another one.
     * @param pageId - The ID of the page to move.
     * @param afterId - The ID of the page to move after.
     */
    movePageAfter: movePageAfterInRevision,

    /**
     * Move a page before another one.
     * @param pageId - The ID of the page to move.
     * @param beforeId - The ID of the page to move before.
     */
    movePageBefore: movePageBeforeInRevision,

    /**
     * Add a cover to a page.
     * @param pageId - The ID of the page to add the cover to.
     * @param ref - The reference to the cover image.
     */
    addPageCover: addPageCoverInRevision,

    /**
     * Remove the cover of a page.
     * @param pageId - The ID of the page to remove the cover of.
     */
    removePageCover: removePageCoverInRevision,

    /**
     * Move the page cover image to the given position.
     * @param pageId - The ID of the page to move the cover of.
     * @param yPos - The new vertical position of the cover image.
     */
    movePageCover: movePageCoverInRevision,

    /**
     * Update the height of the page cover image.
     * @param pageId - The ID of the page to update the height of the cover of.
     * @param height - The new height of the cover image.
     */
    setPageCoverHeight: setPageCoverHeightInRevision,

    /**
     * Reset the height of the page cover image to a height of `undefined`.
     * @param pageId - The ID of the page to reset the height of the cover of.
     */
    resetPageCoverHeight: resetPageCoverHeightInRevision,

    /**
     * Rename a page.
     * @param pageId - The ID of the page to rename.
     * @param title - The new title of the page.
     */
    renamePage: renamePageInRevision,

    /**
     * Update a page's description.
     * @param pageId - The ID of the page to update the description of.
     * @param description - The new description of the page.
     */
    setPageDescription: setPageDescriptionInRevision,

    /**
     * Update the icon/emoji of a page.
     * @param pageId - The ID of the page to update the icon/emoji of.
     * @param update - The update to apply to the icon/emoji.
     */
    setPageIcon: setPageIconInRevision,

    /**
     * Duplicate a page and its descendants.
     */
    duplicatePage: duplicatePageInRevision,

    /**
     * Resolve a conflict in the revision.
     * @param pageId - The ID of the page to resolve the conflict of.
     */
    resolveConflict: resolveConflictInRevision,

    /**
     * Insert a new file.
     * @param file - The file to insert.
     */
    insertFile: insertFileInRevision,

    /**
     * Remove a file by its ID.
     * @param fileId - The ID of the file to remove.
     */
    removeFile: removeFileInRevision,

    /**
     * Update a file in the revision.
     * @param fileId - The ID of the file to update.
     * @param update - The update to apply to the file.
     */
    updateFile: updateFileInRevision,

    /**
     * Mark a file as edited in the revision.
     */
    markFileAsEdited,

    /**
     * Insert reusable content into a revision.
     */
    insertReusableContent: insertReusableContentInRevision,

    /**
     * Remove reusable content from a revision.
     */
    removeReusableContent: removeReusableContentInRevision,

    /**
     * Update reusable content in a revision.
     */
    updateReusableContent: updateReusableContentInRevision,

    /**
     * Insert a new tag into the revision.
     * @param tag - The tag to insert (must include slug and label).
     */
    insertTag: insertTagInRevision,
    insertTagGroup: insertTagGroupInRevision,
    updateTagGroup: updateTagGroupInRevision,
    removeTagGroup: removeTagGroupInRevision,
    setTagGroup: setTagGroupInRevision,

    /**
     * Update an existing tag's properties (label).
     * @param slug - The tag's slug to update.
     * @param update - The properties to update.
     */
    updateTag: updateTagInRevision,

    /**
     * Remove a tag from the revision.
     * @param slug - The tag's slug to remove.
     */
    removeTag: removeTagInRevision,

    /**
     * Get the parent of a page. It returns undefined if the parent is the root page.
     * @param pageId - The ID of the page to get the parent of.
     * @returns The parent page, or undefined if the parent is the root page.
     */
    getPageParent: getPageParentInRevision,

    /**
     * Get a unique title for a new page to insert.
     * @param title - The title to use as a base.
     * @return A unique title.
     */
    getUniquePageTitle,

    /**
     * Update the variables of the revision.
     */
    updateRevisionVariables,

    /**
     * Update the variables on a new page.
     */
    updatePageVariables: updateRevisionPageVariables,

    /**
     * Mark the revision as edited by a user.
     */
    markRevisionAsEdited,

    /**
     * Mark a page as edited by a user.
     */
    markPageAsEdited,

    /**
     * Toggle the visibility states of a page.
     * @param pageId - The ID of the page to toggle the hidden state of.
     * @param hidden - The update to visibility state of the page.
     */
    updatePageVisibility: updatePageVisibilityInRevision,

    /**
     * Update the conditional expression of a page.
     */
    updatePageCondition: updatePageConditionInRevision,

    /**
     * Update the conditional expression of a page.
     */
    removePageCondition: removePageConditionInRevision,

    /**
     * Update the canonical meta link of a page.
     */
    updatePageCanonicalLink,

    /**
     * Add an alternate meta link to a page.
     */
    addPageAlternateLink,

    /**
     * Update the alternate meta link of a page.
     */
    updatePageAlternateLink,
};

/**
 * The change object, grouping all the methods to mutate the revision.
 * It mimics Slate's change API.
 */
export type RevisionChange<R extends YRevision | Revision = YRevision | Revision> = {
    [key in keyof typeof METHODS]: (typeof METHODS)[key] extends (
        revision: any,
        ...args: infer P
    ) => any
        ? (...args: P) => ReturnType<(typeof METHODS)[key]>
        : never;
} & {
    revision: R;
};

export function createRevisionChange<R extends YRevision | Revision>(
    revision: R
): RevisionChange<R> {
    // @ts-ignore
    const change: RevisionChange<R> = {
        revision,
    };

    for (const key in METHODS) {
        const methodKey = key as keyof typeof METHODS;
        // @ts-expect-error - forwarder assigned to a slot typed as the union of all method signatures
        change[methodKey] = (...args: any[]) => {
            if (
                mutations.get(revision as mutations.MutableData<Revision>, 'type') ===
                RevisionType.Computed
            ) {
                throw new Error('Computed revisions are not supported');
            }

            // `METHODS[methodKey]` is a union of the individual method signatures, which is not
            // directly callable; every method takes `(revision, ...args)`, so forward through a
            // generic callable signature.
            return (METHODS[methodKey] as (...args: any[]) => unknown)(revision, ...args);
        };
    }

    return change;
}
